import { type RefObject, useEffect, useRef } from "react";

const MIN_SWIPE_DIST = 80;

export interface SwipeableComponentRef {
	swipeLeft: () => void;
	swipeRight: () => void;
}

export const useSwipeable = (
	componentRef: RefObject<SwipeableComponentRef | null>,
	automaticSwipe = true,
	delay = 5000,
) => {
	const interval = useRef<NodeJS.Timeout | null>(null);
	const touchStartX = useRef<number | null>(null);
	const touchEndX = useRef<number | null>(null);

	useEffect(() => {
		startChange();

		return () => stopChange();
	}, [automaticSwipe, delay]);

	const startChange = () => {
		if (!automaticSwipe) return;

		interval.current = setInterval(() => {
			componentRef.current?.swipeRight();
		}, delay);
	};

	const stopChange = () => {
		if (interval.current) clearTimeout(interval.current);
	};

	const handleTouchStart = (e: React.TouchEvent) => {
		touchStartX.current = e.touches[0].clientX;
	};

	const handleTouchMove = (e: React.TouchEvent) => {
		touchEndX.current = e.touches[0].clientX;
	};

	const handleTouchEnd = () => {
		if (touchStartX.current === null || touchEndX.current === null) return;

		const deltaX = touchEndX.current - touchStartX.current;

		if (Math.abs(deltaX) > MIN_SWIPE_DIST) {
			if (deltaX > 0) {
				componentRef.current?.swipeLeft();
			} else {
				componentRef.current?.swipeRight();
			}
		}

		touchStartX.current = null;
		touchEndX.current = null;
	};

	return {
		handleTouchStart,
		handleTouchMove,
		handleTouchEnd,
		startChange,
		stopChange,
	};
};
